import { useMemo, useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { SpinningLoader, styles } from '@frontend/design-system';
import {
  AppointmentTypeSelectionStep,
  DEFAULT_DURATION,
  DURATION_OPTIONS,
} from '../../components/onboarding-flow-components/appointment-type-selection-step';
import {
  APPOINTMENT_TYPES_PRACTICE_VERTICAL_MAP,
  PracticeVertical,
} from '../../components/onboarding-flow-components/constants';
import { useStackedCardWizardContext } from '../../components/onboarding-flow-components/stacked-card-wizard/stacked-card-wizard-context';
import { StackedCardWizardStep } from '../../components/onboarding-flow-components/stacked-card-wizard/stacked-card-wizard-step-compound';
import { useStepIndexContext } from '../../components/onboarding-flow-components/stacked-card-wizard/step-index-context';
import { useStepData } from '../../components/onboarding-flow-components/stacked-card-wizard/use-step-data';
import { useOnboardingFlowInfo } from '../../hooks/onboarding-flow-hooks/use-onboarding-flow-info';
import { useGetCustomContactSourceId } from '../../hooks/scheduler-v3/use-get-custom-contact-source-id';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { trackOnboardingFlow } from '../../utils/track-onboarding-flow';
import { AppointmentTypeStepData, OnboardingStep, VerticalStepData } from './types';

export const AppointmentTypeStep = () => {
  const { currentStep } = useStackedCardWizardContext();
  const stepIndex = useStepIndexContext();

  // Do not render this step content until active to avoid fetching
  // appointment types until location is selected in prev step
  if (currentStep !== stepIndex) {
    return null;
  }

  return <AppointmentTypeSelection />;
};

// TODO: Implement fetching of appointment types from the backend based on the selected location and other relevant parameters.
const AppointmentTypeSelection = () => {
  const { t } = useTranslation('schedule-onboarding');
  const { parentLocationId, isMultiLocation } = useSchedulingLocationInfo();
  const { customContactSourceId } = useGetCustomContactSourceId();
  const { onboardingFlowInfo, setOnboardingFlowInfo } = useOnboardingFlowInfo();
  const { mergeData, getData } = useStepData<AppointmentTypeStepData>(OnboardingStep.AppointmentType);
  const { getAllData } = useStackedCardWizardContext();

  const stepData = getData();
  const stepperData = getAllData();
  const vertical = stepperData.vertical as VerticalStepData;
  const locationId = (stepperData.locationId as string) || onboardingFlowInfo.locationId || '';
  const initialAppointmentTypeId = stepData.appointmentTypeId || onboardingFlowInfo.appointmentTypeId || '';

  const [selectedAppointmentTypeId, setSelectedAppointmentTypeId] = useState(initialAppointmentTypeId);
  const [serviceName, setServiceName] = useState(stepData.serviceName);
  const [duration, setDuration] = useState<number | 'Custom'>(stepData.duration || DEFAULT_DURATION);
  const [customDuration, setCustomDuration] = useState(stepData.customDuration);

  const handleAppointmentTypeIdSelection = (value: string) => {
    setSelectedAppointmentTypeId(value);

    // Set duration dropdown to the selected appointment type duration and customDuration if not matched
    if (!!fetchedAppointments.length) {
      const selectedOption = fetchedAppointments.find((option) => option.id === value);
      const selectedDuration = selectedOption?.details?.dur || 60;
      setDuration(DURATION_OPTIONS.includes(selectedDuration) ? selectedDuration : 'Custom');
      setCustomDuration(Number(selectedOption?.details?.dur) || undefined);
    }
  };

  // Fetch appointment types
  // TODO: Replace this with actual API call to fetch appointment types based on locationId.
  const fetchedAppointments = [{ id: 'OTHER', displayName: 'Other Service', details: { dur: 30 } }];

  const appointmentTypeOptions = useMemo(() => {
    const apptTypes: { id: string; name: string }[] = [];

    if (fetchedAppointments.length > 0) {
      fetchedAppointments.map(({ id, displayName }) => {
        apptTypes.push({
          id,
          name: displayName || '',
        });
      });
    } else {
      APPOINTMENT_TYPES_PRACTICE_VERTICAL_MAP[vertical?.practiceVertical as PracticeVertical]?.map((name) => {
        apptTypes.push({
          id: '',
          name: name,
        });
      });
    }

    // other option
    apptTypes.push({
      id: 'OTHER',
      name: t('Other Service'),
    });

    return apptTypes;
  }, [fetchedAppointments]);

  // Create appointment type

  const onNextClick = async () => {
    if (selectedAppointmentTypeId) {
      let appointmentTypeId = selectedAppointmentTypeId;
      let appointmentName = appointmentTypeOptions.find((option) => option.id === appointmentTypeId)?.name;

      if (fetchedAppointments.length === 0 || selectedAppointmentTypeId === 'OTHER') {
        // TODO: Replace this with actual API call to create appointment type
        console.log('Creating appointment type for OTHER option', {
          displayName: serviceName || selectedAppointmentTypeId || '',
          locationId: locationId,
          sourceTenantId: locationId,
          details: {
            act: true,
            dur: duration === 'Custom' ? customDuration : duration,
            sobs: false,
          },
          hidden: false,
          // for non-integrated location, we need to set the sourceId as customContactSourceId,
          // for integrated location, BE set the sourceId as the integration's SourceID automatically and on FE we were not allowing create appt-type for integrated location
          sourceId: customContactSourceId,
          parentLocationId: isMultiLocation ? parentLocationId : undefined,
        });
        const res = { appointmentType: { id: 'OTHER', displayName: serviceName || selectedAppointmentTypeId || '' } };
        appointmentTypeId = res?.appointmentType?.id;
        appointmentName = res?.appointmentType?.displayName;
      }

      mergeData({
        appointmentTypeId,
        appointmentName,
        serviceName,
        duration,
        customDuration,
      });

      // Set user preference
      if (appointmentTypeId !== onboardingFlowInfo.appointmentTypeId) {
        setOnboardingFlowInfo({ appointmentTypeId });
      }
    }
    trackOnboardingFlow('wizardAppointmentTypeStepCompleted');
  };

  const isValid = useMemo(() => {
    if (!selectedAppointmentTypeId) return false;

    if (selectedAppointmentTypeId === 'OTHER' && !serviceName) {
      return false;
    }

    if (!(duration || customDuration)) return false;

    if (duration === 'Custom' && !customDuration) {
      return false;
    }

    return true;
  }, [selectedAppointmentTypeId, serviceName, duration, customDuration]);

  const isLoading = false;
  return (
    <StackedCardWizardStep stepId={OnboardingStep.AppointmentType} canGoNext={isValid} onNext={onNextClick}>
      <StackedCardWizardStep.Header
        title={t("What's your most booked appointment?")}
        description={t("Don't worry, you can add more appointment types later")}
      />
      <StackedCardWizardStep.Content>
        <>
          {isLoading ? (
            <div css={styles.flexCenter}>
              <SpinningLoader size='medium' />
            </div>
          ) : (
            <div css={{ maxHeight: 260, overflow: 'auto' }}>
              <AppointmentTypeSelectionStep
                options={appointmentTypeOptions}
                selectedOption={selectedAppointmentTypeId}
                serviceType={serviceName}
                duration={duration}
                customDuration={customDuration}
                setSelectedOption={handleAppointmentTypeIdSelection}
                setServiceType={(value) => setServiceName(value)}
                setDuration={(value) => setDuration(value)}
                setCustomDuration={(value) => setCustomDuration(value)}
                disableDuration={!!fetchedAppointments.length && selectedAppointmentTypeId !== 'OTHER'} // If fetched appointment type is selected
              />
            </div>
          )}
        </>
      </StackedCardWizardStep.Content>

      <StackedCardWizardStep.Footer />
    </StackedCardWizardStep>
  );
};
